import {InfoTooltip} from '@wandb/components';
import NumberInput from '@wandb/weave/common/components/elements/NumberInput';
import {TEAL_500} from '@wandb/weave/common/css/color.styles';
import {Slider} from '@wandb/weave/components';
import React from 'react';
import {twMerge} from 'tailwind-merge';

import {getIndexFromSliderValue} from '../utils/stepUtils';

type MediaSliderProps = {
  label: string;
  min: number;
  max: number;
  current: number;
  onChange: (value: number) => void;
  tooltipContent: React.ReactNode;
  strideLength: number;
  dataTestId: string;
  ticks: number[];
  showTicks?: boolean;
  snapToExistingStep?: boolean;
};

export const MediaSlider = ({
  label,
  min,
  max,
  current,
  onChange,
  tooltipContent,
  strideLength,
  dataTestId,
  ticks,
  showTicks = true,
  snapToExistingStep = false,
}: MediaSliderProps) => {
  const sliderRef = React.useRef<HTMLDivElement>(null);
  const sliderValue = ticks.indexOf(current) ?? 0;

  // Calculate thumb radius as percentage of actual track width
  const thumbRadiusPercent = React.useMemo(() => {
    const trackWidth = sliderRef.current?.offsetWidth || 400;
    return (7 / trackWidth) * 100; // 11px radius
  }, []);
  return (
    <div className="flex h-[48px] w-full max-w-[400px] items-center gap-4">
      <div className="flex items-center w-fit gap-3">
        <span className="text-sm text-moon-800 whitespace-nowrap">{label}</span>
        <InfoTooltip id={label.toLowerCase()} aria-label={label} ml={1}>
          {tooltipContent}
        </InfoTooltip>
      </div>

      <div className="flex-1" data-test={dataTestId}>
        <div className="relative pr-4" ref={sliderRef}>
          <Slider.Root
            className="MediaSlider"
            name={label.toLowerCase()}
            min={0}
            max={ticks.length - 1}
            step={strideLength}
            orientation="horizontal"
            onValueChange={([newVal]) => {
              onChange(ticks[newVal]);
            }}
            value={[sliderValue]}>
            <Slider.Track className="flex-1">
              <Slider.Range
                className="flex-1 grow"
                style={{
                  backgroundColor: TEAL_500,
                }}
              />
            </Slider.Track>
            <Slider.Thumb
              className={twMerge(
                'MediaSliderThumb h-14 w-14 bg-teal-450 border-transparent',
                'focus:ring-2 focus:ring-teal-500 active:ring-2 active:ring-teal-500',
                'hover:ring-2 hover:ring-teal-450',
                'focus:border-[2px] focus:border-white',
                'active:border-teal-450'
              )}
              onPointerUp={() => {
                if (document.activeElement instanceof HTMLElement) {
                  document.activeElement.blur();
                }
              }}
            />
          </Slider.Root>

          {showTicks && ticks.length > 0 && (
            <div className="absolute left-0 right-0 top-full mt-2">
              {ticks.map(tick => {
                // Account for thumb radius on both sides
                const usableRange = 100 - 2 * thumbRadiusPercent;
                const position =
                  ((tick - min) / (max - min)) * usableRange +
                  thumbRadiusPercent;

                return (
                  <div
                    key={tick}
                    className="absolute -translate-x-1/2 transform"
                    style={{
                      left: `${position}%`,
                    }}>
                    <div className="h-6 w-2 rounded-full bg-moon-300" />
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      <div className="flex-shrink-0">
        <NumberInput
          className="w-full px-2 text-sm text-moon-800"
          inputStyle={{
            fontSize: '14px',
          }}
          value={current}
          onChange={newVal => {
            if (newVal !== undefined) {
              if (snapToExistingStep) {
                const newIdx = getIndexFromSliderValue(
                  newVal,
                  current,
                  ticks,
                  strideLength
                );
                if (newIdx !== undefined) {
                  onChange(ticks[newIdx]);
                  return;
                }
              }
              onChange(newVal);
            }
          }}
          stepper={true}
          useStepperPlusMinus={true}
          strideLength={strideLength}
        />
      </div>
    </div>
  );
};
